/**
 * FHIR-related models for Symphony applications
 *
 * These models represent FHIR Questionnaire structure and conversion artifacts. Based on FHIR R4 specification.
 */

/** FHIR Questionnaire resource (R4) */
export interface FHIRQuestionnaire {
  resourceType: 'Questionnaire';
  id?: string;
  url?: string;
  version?: string;
  status: 'draft' | 'active' | 'retired';
  title?: string;
  description?: string;
  publisher?: string;
  date?: string; // ISO 8601
  contact?: FHIRContact[];
  item: FHIRItem[];
  extension?: FHIRExtension[];
  meta?: FHIRMeta;
}

/** FHIR Questionnaire item (question or group) */
export interface FHIRItem {
  linkId: string; // Unique within questionnaire
  definition?: string; // ElementDefinition URI
  code?: FHIRCoding[]; // Corresponding concept(s) for this item
  prefix?: string; // E.g. "1(a)", "2.5.3"
  type: FHIRItemType;
  text?: string; // Display text
  questionTitle?: string; // Question title from control properties for display in property panel
  required?: boolean;
  readOnly?: boolean;
  repeats?: boolean; // Allow multiple answers
  maxLength?: number; // Maximum length for string values
  answerValueSet?: string; // Canonical URL of ValueSet with permitted answers
  enableBehavior?: 'all' | 'any'; // Controls how multiple enableWhen values are interpreted
  initial?: FHIRInitialValue[];
  answerOption?: FHIRAnswerOption[];
  enableWhen?: FHIREnableWhen[];
  item?: FHIRItem[]; // Nested items
  extension?: FHIRExtension[];
}

/** FHIR item types */
export type FHIRItemType =
  | 'group'
  | 'display'
  | 'boolean'
  | 'decimal'
  | 'integer'
  | 'date'
  | 'dateTime'
  | 'time'
  | 'string'
  | 'text'
  | 'url'
  | 'choice'
  | 'open-choice'
  | 'attachment'
  | 'reference'
  | 'quantity';

/** FHIR answer option */
export interface FHIRAnswerOption {
  valueCoding?: {
    system?: string;
    code: string;
    display: string;
  };
  valueInteger?: number;
  valueDate?: string;
  valueTime?: string;
  valueString?: string;
  initialSelected?: boolean;
}

/** FHIR initial value */
export interface FHIRInitialValue {
  valueBoolean?: boolean;
  valueDecimal?: number;
  valueInteger?: number;
  valueDate?: string;
  valueDateTime?: string;
  valueTime?: string;
  valueString?: string;
  valueUri?: string; // URI reference value
  valueCoding?: {
    system?: string;
    code: string;
    display: string;
  };
}

/** FHIR enable when (conditional display) */
export interface FHIREnableWhen {
  question: string; // linkId of source question
  operator: 'exists' | '=' | '!=' | '>' | '<' | '>=' | '<=' | 'contains';
  answerBoolean?: boolean;
  answerDecimal?: number;
  answerInteger?: number;
  answerDate?: string;
  answerDateTime?: string;
  answerTime?: string;
  answerString?: string;
  answerCoding?: {
    system?: string;
    code: string;
    display?: string;
  };
}

/** FHIR extension (custom metadata) */
export interface FHIRExtension {
  url: string;
  valueString?: string;
  valueInteger?: number;
  valueDecimal?: number; // Decimal extension value
  valueBoolean?: boolean;
  valueCode?: string;
  valueCoding?: {
    system?: string;
    code: string;
    display?: string;
  };
  valueCodeableConcept?: {
    coding?: Array<{
      system?: string;
      code: string;
      display?: string;
    }>;
    text?: string;
  }; // CodeableConcept extension value
  // Support for complex extensions (nested extensions)
  extension?: FHIRExtension[];
}

/** FHIR contact */
export interface FHIRContact {
  name?: string;
  telecom?: Array<{
    system: 'phone' | 'fax' | 'email' | 'pager' | 'url' | 'sms' | 'other';
    value: string;
    use?: 'home' | 'work' | 'temp' | 'old' | 'mobile';
  }>;
}

/** FHIR validation result */
export interface FHIRValidationResult {
  valid: boolean;
  errors: FHIRValidationError[];
  warnings: FHIRValidationWarning[];
}

/** FHIR validation error */
export interface FHIRValidationError {
  severity: 'error';
  location: string; // FHIRPath to element
  message: string;
  code?: string;
}

/** FHIR validation warning */
export interface FHIRValidationWarning {
  severity: 'warning';
  location: string;
  message: string;
  code?: string;
}

/** Control type to FHIR type mapping */
export const CONTROL_TO_FHIR_TYPE_MAP: Record<string, FHIRItemType> = {
  'text-input-v1': 'string',
  'text-area-v1': 'text',
  'number-input-v1': 'integer',
  'decimal-input-v1': 'decimal',
  'date-picker-v1': 'date',
  'datetime-picker-v1': 'dateTime',
  'time-picker-v1': 'time',
  'checkbox-v1': 'boolean',
  'radio-group-v1': 'choice',
  'select-dropdown-v1': 'choice',
  'multi-select-v1': 'open-choice',
  'card-link-group-v1': 'group',
};

// ============================================================================
// FHIR R4 QuestionnaireResponse
// ============================================================================

/** FHIR R4 QuestionnaireResponse resource */
export interface FHIRQuestionnaireResponse {
  resourceType: 'QuestionnaireResponse';
  id?: string;
  questionnaire?: string;
  status: 'in-progress' | 'completed' | 'amended' | 'entered-in-error' | 'stopped';
  subject?: FHIRReference;
  authored?: string; // ISO 8601
  author?: FHIRReference;
  source?: FHIRReference;
  encounter?: FHIRReference;
  item?: FHIRQuestionnaireResponseItem[];
  extension?: FHIRExtension[];
  meta?: FHIRMeta;
}

/** FHIR R4 QuestionnaireResponse item */
export interface FHIRQuestionnaireResponseItem {
  linkId: string;
  text?: string;
  questionTitle?: string; // Question title from control properties for display in property panel
  answer?: FHIRQuestionnaireResponseAnswer[];
  item?: FHIRQuestionnaireResponseItem[];
  extension?: FHIRExtension[];
}

/** FHIR R4 QuestionnaireResponse answer */
export interface FHIRQuestionnaireResponseAnswer {
  valueBoolean?: boolean;
  valueDecimal?: number;
  valueInteger?: number;
  valueDate?: string;
  valueDateTime?: string;
  valueTime?: string;
  valueString?: string;
  valueUri?: string;
  valueCoding?: { system?: string; code: string; display?: string };
  valueQuantity?: { value?: number; unit?: string; system?: string; code?: string };
  valueAttachment?: {
    contentType?: string;
    language?: string;
    data?: string;
    url?: string;
    size?: number;
    hash?: string;
    title?: string;
    creation?: string;
  };
  valueReference?: FHIRReference;
  item?: FHIRQuestionnaireResponseItem[];
  /** Mapped medical codes for the selected option (ICD, CPT, SNOMED, etc.) */
  mappedCodes?: Array<{ code: string; display: string }>;
  /** Medical code type (e.g., 'ICD', 'CPT', 'SNOMED') */
  type?: string;
}

// ============================================================================
// FHIR Utility Types (used in responses and extensions)
// ============================================================================

/** FHIR Coding (used in valueCoding and extensions) */
export interface FHIRCoding {
  system?: string;
  version?: string;
  code: string;
  display?: string;
  userSelected?: boolean;
}

/** FHIR CodeableConcept (used in answer options and extensions) */
export interface FHIRCodeableConcept {
  coding?: FHIRCoding[];
  text?: string;
}

/** FHIR Quantity (used in valueQuantity) */
export interface FHIRQuantity {
  value?: number;
  comparator?: '<' | '<=' | '>=' | '>';
  unit?: string;
  system?: string;
  code?: string;
}

/** FHIR Attachment (used in valueAttachment) */
export interface FHIRAttachment {
  contentType?: string;
  language?: string;
  data?: string;
  url?: string;
  size?: number;
  hash?: string;
  title?: string;
  creation?: string;
}

// ============================================================================
// FHIR R4 Clinical Resource Types
// ============================================================================

/** FHIR R4 Meta tag (coding entry in meta.tag array) */
export interface FHIRMetaTag {
  system?: string;
  code: string;
  display?: string;
}

/** FHIR R4 Meta element */
export interface FHIRMeta {
  versionId?: string;
  lastUpdated?: string;
  tag?: FHIRMetaTag[];
}

/** FHIR R4 Reference */
export interface FHIRReference {
  reference?: string;
  type?: string;
  display?: string;
  identifier?: FHIRIdentifier;
}

/** FHIR R4 Identifier */
export interface FHIRIdentifier {
  system?: string;
  value?: string;
  use?: 'usual' | 'official' | 'temp' | 'secondary' | 'old';
}

/** FHIR R4 HumanName */
export interface FHIRHumanName {
  use?: 'usual' | 'official' | 'temp' | 'nickname' | 'anonymous' | 'old' | 'maiden';
  text?: string;
  family?: string;
  given?: string[];
  prefix?: string[];
  suffix?: string[];
}

/** FHIR R4 ContactPoint */
export interface FHIRContactPoint {
  system?: 'phone' | 'fax' | 'email' | 'pager' | 'url' | 'sms' | 'other';
  value?: string;
  use?: 'home' | 'work' | 'temp' | 'old' | 'mobile';
}

/** FHIR R4 Address */
export interface FHIRAddress {
  use?: 'home' | 'work' | 'temp' | 'old' | 'billing';
  type?: 'postal' | 'physical' | 'both';
  text?: string;
  line?: string[];
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

/** FHIR R4 Period */
export interface FHIRPeriod {
  start?: string;
  end?: string;
}

/** FHIR R4 Patient resource */
export interface FHIRPatient {
  resourceType: 'Patient';
  id?: string;
  identifier?: FHIRIdentifier[];
  active?: boolean;
  name?: FHIRHumanName[];
  telecom?: FHIRContactPoint[];
  gender?: 'male' | 'female' | 'other' | 'unknown';
  birthDate?: string;
  address?: FHIRAddress[];
  extension?: FHIRExtension[];
  meta?: FHIRMeta;
}

/** FHIR R4 Practitioner resource */
export interface FHIRPractitioner {
  resourceType: 'Practitioner';
  id?: string;
  identifier?: FHIRIdentifier[];
  active?: boolean;
  name?: FHIRHumanName[];
  telecom?: FHIRContactPoint[];
  gender?: 'male' | 'female' | 'other' | 'unknown';
  birthDate?: string;
  address?: FHIRAddress[];
  extension?: FHIRExtension[];
  meta?: FHIRMeta;
}

/** FHIR R4 Organization resource */
export interface FHIROrganization {
  resourceType: 'Organization';
  id?: string;
  identifier?: FHIRIdentifier[];
  active?: boolean;
  type?: FHIRCodeableConcept[];
  name?: string;
  telecom?: FHIRContactPoint[];
  address?: FHIRAddress[];
  partOf?: FHIRReference;
  extension?: FHIRExtension[];
}

/** FHIR R4 RelatedPerson resource */
export interface FHIRRelatedPerson {
  resourceType: 'RelatedPerson';
  id?: string;
  identifier?: FHIRIdentifier[];
  active?: boolean;
  patient: FHIRReference;
  relationship?: FHIRCodeableConcept[];
  name?: FHIRHumanName[];
  telecom?: FHIRContactPoint[];
  gender?: 'male' | 'female' | 'other' | 'unknown';
  birthDate?: string;
  address?: FHIRAddress[];
  extension?: FHIRExtension[];
}

/** FHIR R4 Encounter resource */
export interface FHIREncounter {
  resourceType: 'Encounter';
  id?: string;
  identifier?: FHIRIdentifier[];
  status:
    | 'planned'
    | 'arrived'
    | 'triaged'
    | 'in-progress'
    | 'onleave'
    | 'finished'
    | 'cancelled'
    | 'entered-in-error'
    | 'unknown';
  class?: { system?: string; code: string; display?: string };
  type?: FHIRCodeableConcept[];
  subject?: FHIRReference;
  participant?: Array<{ type?: FHIRCodeableConcept[]; individual?: FHIRReference }>;
  period?: FHIRPeriod;
  reasonCode?: FHIRCodeableConcept[];
  serviceProvider?: FHIRReference;
  extension?: FHIRExtension[];
}

/** FHIR R4 Location resource */
export interface FHIRLocation {
  resourceType: 'Location';
  id?: string;
  identifier?: FHIRIdentifier[];
  status?: 'active' | 'suspended' | 'inactive';
  name?: string;
  description?: string;
  type?: FHIRCodeableConcept[];
  telecom?: FHIRContactPoint[];
  address?: FHIRAddress;
  managingOrganization?: FHIRReference;
  extension?: FHIRExtension[];
}

/** FHIR R4 Appointment resource */
export interface FHIRAppointment {
  resourceType: 'Appointment';
  id?: string;
  meta?: FHIRMeta;
  status:
    | 'proposed'
    | 'pending'
    | 'booked'
    | 'arrived'
    | 'fulfilled'
    | 'cancelled'
    | 'noshow'
    | 'entered-in-error'
    | 'checked-in'
    | 'waitlist';
  encounterDate?: string;
  appointmentConfirmationId?: string;
  start?: string;
  end?: string;
  description?: string;
  participant: Array<{
    actor?: { reference?: string; display?: string };
    status: 'accepted' | 'declined' | 'tentative' | 'needs-action';
  }>;
  extension?: FHIRExtension[];
}
